import { StandardRequest, StandardResponse } from "./functions";

/**
 * @swagger
 *  components:
 *    schemas:
 *      Memory:
 *        type: object
 *        required: [ "ownerId", "title", "date", "description" ]
 *        properties:
 *          id:
 *            type: string
 *          ownerId:
 *            type: string
 *          title:
 *            type: string
 *          date:
 *            type: string
 *            description: Date representation, either formatted string or serialized JSON DatePayload
 *          location:
 *            type: string
 *          description:
 *            type: string
 *          createdAt:
 *            type: number
 */
import { MediaModel } from "./media";
import { ILocation } from "./location";
import { ITagAssociationMeta, TagModel } from "./tag";
import { IEntityContactMeta, IEntityGroupMeta, IEntityMediaMeta, IEntityPlaceMeta, IEntityAnchorMeta } from "./entity-links";
import type { ContactModel } from "./contacts";
import type { GroupModel } from "./groups";
import type { PlaceModel } from "./places";
import type { AnchorModel } from "./anchors";
import { IUnifiedFilter } from "./filters";
/**
 * Raw persisted database document schema for a Memory entity in Firestore (`memories/{id}`).
 */
export interface IMemory {
  id?: string;
  ownerId: string;
  title: string;
  date: string;
  location?: ILocation;
  description: string;
  favouriteRating?: number;
  media?: Record<string, IEntityMediaMeta>;
  /** Companion synchronized array of media IDs maintained for Firestore array-contains query indexing */
  mediaIds?: string[];
  /** Foreign key relationship map of associated contacts Record<contactId, IEntityContactMeta> */
  contacts?: Record<string, IEntityContactMeta>;
  /** Companion synchronized array of contact IDs maintained for Firestore query indexing */
  contactIds?: string[];
  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Companion synchronized array of group IDs maintained for Firestore query indexing */
  groupIds?: string[];
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Companion synchronized array of tag IDs maintained for Firestore query indexing */
  tagIds?: string[];
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  /** Companion synchronized array of place IDs maintained for Firestore query indexing */
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  /** Companion synchronized array of anchor IDs maintained for Firestore query indexing */
  anchorIds?: string[];
  /** Primary photo media ID foreign key referencing media.id */
  photoMediaId?: string;
  createdAt: number;
  updatedAt?: number;
}

export type Memory = IMemory;

import { ShareSummary, SharedToSummary } from "./shares";

export type MemoryModel = Memory & {
  /**
   * Canonical hydrated Media view models associated with this memory.
   * Matches the entity pattern (contactItems, groupItems, mediaItems).
   */
  mediaItems?: MediaModel[];
  contactItems?: ContactModel[];
  groupItems?: GroupModel[];
  placeItems?: PlaceModel[];
  anchorItems?: AnchorModel[];
  /**
   * Fully hydrated Tag models associated with this memory.
   */
  tagItems?: TagModel[];
  photoUrl?: string;
  photoIconUrl?: string;
  userRole?: "owner" | "editor" | "viewer";
  canShare?: boolean;
  sharedWith?: ShareSummary[];
  sharedTo?: SharedToSummary[];
  ownerDisplayName?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      CreateMemoryRequest:
 *        type: object
 *        required: [ "title", "date", "description" ]
 *        properties:
 *          title:
 *            type: string
 *          date:
 *            type: string
 *            description: Date representation, either formatted string or serialized JSON DatePayload
 *          location:
 *            $ref: '#/components/schemas/Location'
 *          description:
 *            type: string
 *          media:
 *            type: object
 *            additionalProperties:
 *              $ref: '#/components/schemas/MemoryMediaMeta'
 */
export type CreateMemoryRequest = StandardRequest & {
  title: string;
  date: string;
  location?: ILocation;
  description: string;
  favouriteRating?: number;
  photoMediaId?: string;
  media?: Record<string, IEntityMediaMeta>;
  mediaIds?: string[];
  contacts?: Record<string, IEntityContactMeta>;
  contactIds?: string[];
  groups?: Record<string, IEntityGroupMeta>;
  groupIds?: string[];
  tags?: Record<string, ITagAssociationMeta>;
  tagIds?: string[];
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      CreateMemoryResponse:
 *        type: object
 *        required: [ "memoryId" ]
 *        properties:
 *          memoryId:
 *            type: string
 *          memory:
 *            $ref: '#/components/schemas/Memory'
 */
export type CreateMemoryResponse = StandardResponse & {
  memoryId: string;
  memory?: MemoryModel;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      EditMemoryRequest:
 *        type: object
 *        required: [ "memoryId" ]
 *        properties:
 *          memoryId:
 *            type: string
 *          title:
 *            type: string
 *          date:
 *            type: string
 *            description: Date representation, either formatted string or serialized JSON DatePayload
 *          location:
 *            $ref: '#/components/schemas/Location'
 *          description:
 *            type: string
 *          media:
 *            type: object
 *            additionalProperties:
 *              $ref: '#/components/schemas/MemoryMediaMeta'
 */
export type EditMemoryRequest = StandardRequest & {
  memoryId: string;
  title?: string;
  date?: string;
  location?: ILocation;
  description?: string;
  favouriteRating?: number;
  photoMediaId?: string;
  media?: Record<string, IEntityMediaMeta>;
  contacts?: Record<string, IEntityContactMeta>;
  groups?: Record<string, IEntityGroupMeta>;
  tags?: Record<string, ITagAssociationMeta>;
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMemoryRequest:
 *        type: object
 *        required: [ "memoryId" ]
 *        properties:
 *          memoryId:
 *            type: string
 */
export type GetMemoryRequest = StandardRequest & {
  memoryId: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      DeleteMemoryRequest:
 *        type: object
 *        required: [ "memoryId" ]
 *        properties:
 *          memoryId:
 *            type: string
 *          cascadeUnlink:
 *            type: boolean
 */
export type DeleteMemoryRequest = StandardRequest & {
  memoryId: string;
  cascadeUnlink?: boolean;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMemoryResponse:
 *        type: object
 *        required: [ "memory" ]
 *        properties:
 *          memory:
 *            $ref: '#/components/schemas/MemoryModel'
 */
export type GetMemoryResponse = StandardResponse & {
  memory: MemoryModel;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyMemoriesRequest:
 *        type: object
 *        properties: {}
 */
export type GetMyMemoriesRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyMemoriesResponse:
 *        type: object
 *        required: [ "memories" ]
 *        properties:
 *          memories:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/MemoryModel'
 */
export type GetMyMemoriesResponse = StandardResponse & {
  memories: MemoryModel[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      MemoryFilter:
 *        type: object
 *        properties:
 *          name:
 *            type: string
 *            description: Regex pattern to filter memories by title
 */
export type MemoryFilter = {
  name?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMemoriesRequest:
 *        type: object
 *        properties:
 *          filter:
 *            $ref: '#/components/schemas/MemoryFilter'
 */
export type GetMemoriesRequest = StandardRequest & {
  filter?: MemoryFilter | IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMemoriesResponse:
 *        type: object
 *        required: [ "memories" ]
 *        properties:
 *          memories:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/MemoryModel'
 */
export type GetMemoriesResponse = StandardResponse & {
  memories: MemoryModel[];
};
/**
 * @swagger
 *  components:
 *    schemas:
 *      GetAllMemoriesRequest:
 *        type: object
 *        properties: {}
 */
export type GetAllMemoriesRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetAllMemoriesResponse:
 *        type: object
 *        required: [ "memories" ]
 *        properties:
 *          memories:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/MemoryModel'
 */
export type GetAllMemoriesResponse = StandardResponse & {
  memories: MemoryModel[];
};
